/**
 * Tooltip that also works on touch (CLAUDE.md 14.3).
 *
 * "No hover-only affordances -- every clause tooltip must also open on tap and be
 * dismissible." That rule matters more here than in most products: the tooltips carry
 * code clause references, which are the whole point of the transparency promise. A
 * clause an engineer cannot read on a phone may as well not be cited.
 *
 * Radix Tooltip is keyboard- and hover-driven only. So this pairs it with a Popover on
 * coarse pointers, which is tap-to-open, tap-outside-to-dismiss, and Escape-to-close.
 * One component, one API, the right behaviour on each input type.
 *
 * `title=` is banned by an eslint rule for exactly this reason -- see eslint.config.js.
 */
import { useEffect, useState, type ReactNode } from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import * as PopoverPrimitive from '@radix-ui/react-popover'

import { cn } from '@/lib/cn'

export const TooltipProvider = TooltipPrimitive.Provider

/** True when the primary input is touch, so we swap hover for tap. */
function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(false)

  useEffect(() => {
    const query = window.matchMedia('(pointer: coarse)')
    const update = () => setCoarse(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])

  return coarse
}

const contentClass =
  'z-50 max-w-xs rounded-md border border-border bg-surface-raised px-3 py-2 text-sm ' +
  'text-text shadow-[var(--shadow-md)]'

export function Tooltip({
  children,
  content,
  side = 'top',
}: {
  /** The trigger. Must accept a ref, so a plain element or a `forwardRef` component. */
  children: ReactNode
  content: ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
}) {
  const coarse = useCoarsePointer()

  if (coarse) {
    return (
      <PopoverPrimitive.Root>
        <PopoverPrimitive.Trigger asChild>{children}</PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content side={side} sideOffset={6} className={contentClass}>
            {content}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    )
  }

  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content side={side} sideOffset={6} className={cn(contentClass)}>
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}

/**
 * A clause reference that reveals its explanation on hover *or* tap.
 *
 * Clause text itself is never reproduced -- only the reference and our own words
 * (CLAUDE.md 2.3, design-code copyright).
 */
export function ClauseRef({ clause, note }: { clause: string; note?: ReactNode }) {
  if (!note) {
    return <span className="whitespace-nowrap text-xs text-text-muted">{clause}</span>
  }
  return (
    <Tooltip content={note}>
      <button
        type="button"
        className="cursor-help whitespace-nowrap text-xs text-text-muted underline decoration-dotted underline-offset-2"
      >
        {clause}
      </button>
    </Tooltip>
  )
}
